import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoBspark } from "@/components/LogoBspark";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, GraduationCap, CalendarRange, MapPin,
  LogOut, Moon, Sun, Menu, X, ShieldCheck, Palette, UserCog, Receipt, CalendarCheck, ClipboardCheck, Mail, Scale, KeyRound, Euro, BookOpen, Inbox, Search,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

const nav = [
  { to: "/admin/panel", label: "Panel", icon: LayoutDashboard, testid: "nav-panel" },
  { to: "/admin/alumnos", label: "Alumnos", icon: Users, testid: "nav-alumnos" },
  { to: "/admin/calendario", label: "Calendario", icon: CalendarCheck, testid: "nav-calendario" },
  { to: "/admin/pasar-lista", label: "Pasar lista", icon: ClipboardCheck, testid: "nav-pasar-lista" },
  { to: "/admin/buscar-alumno", label: "Buscar alumno", icon: Search, testid: "nav-buscar-alumno" },
  { to: "/admin/padres", label: "Padres", icon: UserCog, testid: "nav-padres" },
  { to: "/admin/clases", label: "Clases", icon: CalendarRange, testid: "nav-clases" },
  { to: "/admin/profesores", label: "Profesores", icon: GraduationCap, testid: "nav-profesores" },
  { to: "/admin/planes", label: "Planes de pago", icon: Receipt, testid: "nav-planes", adminOnly: true },
  { to: "/admin/pagos", label: "Pagos y facturación", icon: Euro, testid: "nav-pagos", adminOnly: true },
  { to: "/admin/comunicaciones", label: "Comunicaciones", icon: Mail, testid: "nav-comunicaciones" },
  { to: "/admin/legal", label: "Textos legales", icon: Scale, testid: "nav-legal", adminOnly: true },
  { to: "/admin/sedes", label: "Sedes", icon: MapPin, testid: "nav-sedes" },
  { to: "/admin/usuarios", label: "Usuarios", icon: ShieldCheck, testid: "nav-usuarios", adminOnly: true },
  { to: "/admin/mensajes", label: "Buzón", icon: Inbox, testid: "nav-mensajes" },
  { to: "/admin/manuales", label: "Manuales de uso", icon: BookOpen, testid: "nav-manuales" },
];

const ROL_LABEL = {
  admin: "Administración",
  staff: "Recepción",
  franquiciado: "Franquiciatario",
  profesor: "Profesor",
};

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("bspark_theme") === "dark");
  const [open, setOpen] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [pass, setPass] = useState({ actual: "", nueva: "" });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("bspark_theme", dark ? "dark" : "light");
  }, [dark]);

  const PROFESOR_PERMITIDO = ["nav-calendario", "nav-pasar-lista", "nav-buscar-alumno",
                              "nav-clases", "nav-mensajes", "nav-manuales"];
  const items = nav.filter((n) => {
    if (user?.role === "profesor") return PROFESOR_PERMITIDO.includes(n.testid);
    if (n.testid === "nav-buscar-alumno") return false;  // el resto ya tiene la vista de Alumnos
    return !n.adminOnly || user?.role === "admin";
  });

  return (
    <div className="min-h-screen bg-background flex">
      {open && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />
      )}
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-64 border-r border-border bg-card flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="px-6 py-7 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <LogoBspark className="h-10" texto={false} testid="logo-panel" />
            <div>
              <p className="font-display font-extrabold text-base leading-none">Family Hub</p>
              <p className="text-xs text-muted-foreground mt-1">Panel interno</p>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pb-3 space-y-1"
          data-testid="sidebar-nav">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/admin/panel"}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border space-y-3 shrink-0">
          <div className="text-sm">
            <p className="font-semibold truncate">{user?.nombre || user?.email}</p>
            <p className="text-xs text-muted-foreground">{ROL_LABEL[user?.role] || user?.role}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 rounded-lg"
              onClick={() => setPassOpen(true)} data-testid="mi-password-btn">
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 rounded-lg"
              onClick={() => setDark((d) => !d)}
              data-testid="theme-toggle"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 rounded-lg"
              onClick={() => {
                logout();
                navigate("/admin");
              }}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0">
        <Dialog open={passOpen} onOpenChange={setPassOpen}>
          <DialogContent className="rounded-2xl" data-testid="mi-password-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Mi contraseña</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label>Contraseña actual</Label>
                <PasswordInput value={pass.actual} data-testid="mi-pass-actual"
                  onChange={(e) => setPass((p) => ({ ...p, actual: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Nueva contraseña</Label>
                <PasswordInput value={pass.nueva} data-testid="mi-pass-nueva"
                  onChange={(e) => setPass((p) => ({ ...p, nueva: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button className="rounded-full font-semibold" data-testid="mi-pass-guardar"
                onClick={async () => {
                  if (pass.nueva.length < 6) return toast.error("La nueva contraseña necesita al menos 6 caracteres");
                  try {
                    await api.post("/mi-password", pass);
                    toast.success("Contraseña actualizada");
                    setPassOpen(false);
                    setPass({ actual: "", nueva: "" });
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "No se pudo cambiar");
                  }
                }}>Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <header className="sticky top-0 z-20 h-16 border-b border-border bg-background/80 backdrop-blur-xl flex items-center gap-3 px-4 lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="open-sidebar">
            <Menu className="h-6 w-6" />
          </button>
          <span className="font-display font-bold text-lg">Bspark</span>
        </header>
        <main className="p-5 sm:p-8 lg:p-10 max-w-[1500px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
